import { FC } from 'react';

import { ResourceType } from '@dbt-labs/dbt-dag';

import { useFilterContext } from '@/context/FilterProvider';
import { useSearchFacets } from '@/context/SearchFacetsProvider';
import {
  accessTypes,
  CUSTOM_MATERIALIZATION_TYPE,
  materializationTypes,
  modelingLayerTypes,
} from '@/typings/dbt';
import { getCustomMaterializationCount } from '@/util/customMaterialization';
import { ClearSelectAllButton } from './ClearSelectAllButton';
import { FilterSection, FilterSectionProps } from './FilterSection';
import { ModelAccessToggle } from './ModelAccessToggle';
import { ModelingLayerToggle } from './ModelingLayerToggle';
import { ModelMaterializationToggle } from './ModelMaterializationToggle';

interface ModelLevelFiltersProps {
  onDive?: (resourceType: ResourceType) => void;
}

export const ModelLevelFilters: FC<ModelLevelFiltersProps> = () => {
  return (
    <>
      <ModelAccessFilters />
      <ModelMaterializationFilters />
      <ModelingLayerFilters />
    </>
  );
};

interface ModelAccessFiltersProps extends FilterSectionProps {}

export const ModelAccessFilters: FC<ModelAccessFiltersProps> = ({
  title = 'Access',
}) => {
  const filterCtx = useFilterContext();
  const { aggregations } = useSearchFacets();
  const selectedAccessLevels = filterCtx.filters?.access;

  return (
    <FilterSection title={title} enableExpansion isDefaultExpanded>
      {accessTypes.map((accessType) => (
        <ModelAccessToggle
          key={accessType}
          accessLevel={accessType}
          isChecked={selectedAccessLevels?.includes(accessType)}
          onToggle={filterCtx.updateFilters}
          count={aggregations?.modelContracts?.[accessType]}
        />
      ))}
      <ClearSelectAllButton
        type="access"
        selectedList={selectedAccessLevels}
        selectionList={accessTypes.map((accessType) => ({
          label: accessType,
          value: accessType,
        }))}
      />
    </FilterSection>
  );
};

interface ModelMaterializationFiltersProps extends FilterSectionProps {}

export const ModelMaterializationFilters: FC<ModelMaterializationFiltersProps> = ({
  title = 'Materialization',
}) => {
  const filterCtx = useFilterContext();
  const { aggregations } = useSearchFacets();
  const selectedMaterializationTypes = filterCtx.filters?.materialization;
  const customCount = getCustomMaterializationCount(aggregations);

  return (
    <FilterSection title={title} enableExpansion>
      {materializationTypes?.map((materializationType) => (
        <ModelMaterializationToggle
          key={materializationType}
          materializationType={materializationType}
          isChecked={selectedMaterializationTypes?.includes(materializationType)}
          onToggle={filterCtx.updateFilters}
          count={aggregations?.dbtMaterializationTypes?.[materializationType]}
        />
      ))}
      <ModelMaterializationToggle
        key={CUSTOM_MATERIALIZATION_TYPE}
        materializationType={CUSTOM_MATERIALIZATION_TYPE}
        isChecked={selectedMaterializationTypes?.includes(CUSTOM_MATERIALIZATION_TYPE)}
        onToggle={filterCtx.updateFilters}
        count={customCount || undefined}
      />
      <ClearSelectAllButton
        type="materialization"
        selectedList={selectedMaterializationTypes}
        selectionList={[
          ...materializationTypes.map((materializationType) => ({
            label: materializationType,
            value: materializationType,
          })),
          {
            label: CUSTOM_MATERIALIZATION_TYPE,
            value: CUSTOM_MATERIALIZATION_TYPE,
          },
        ]}
      />
    </FilterSection>
  );
};

interface ModelingLayerFiltersProps extends FilterSectionProps {}

export const ModelingLayerFilters: FC<ModelingLayerFiltersProps> = ({
  title = 'Modeling Layer',
}) => {
  const filterCtx = useFilterContext();
  const { aggregations } = useSearchFacets();
  const selectedModelingLayerTypes = filterCtx.filters?.modelingLayer;

  return (
    <FilterSection title={title} enableExpansion isDefaultExpanded>
      {modelingLayerTypes?.map((modelingLayerType) => (
        <ModelingLayerToggle
          key={modelingLayerType}
          modelingLayerType={modelingLayerType}
          isChecked={selectedModelingLayerTypes?.includes(modelingLayerType)}
          onToggle={filterCtx.updateFilters}
          count={aggregations?.modelingLayers?.[modelingLayerType]}
        />
      ))}
      <ClearSelectAllButton
        type="modelingLayer"
        selectedList={selectedModelingLayerTypes}
        selectionList={modelingLayerTypes.map((modelingLayerType) => ({
          label: modelingLayerType,
          value: modelingLayerType,
        }))}
      />
    </FilterSection>
  );
};
